import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import clsx from 'clsx';
import { useFirstVisitStore } from '@/state/firstVisitStore';
import { getGuideForPath, guideKey } from '@/data/screenGuides';

/**
 * Top-bar Help button. Behaviour:
 * - If the current route has a guide → open it (force flag bypasses seen state).
 * - If "Don't show these again" is set → also clear the mute so the user is back
 *   to first-visit prompts going forward.
 * - If no guide exists for the current route → reset all "seen" flags so the
 *   next routes the user visits will pop their guide again.
 */
export default function HelpLauncher() {
  const location = useLocation();
  const globallyMuted = useFirstVisitStore((s) => s.globallyMuted);
  const force = useFirstVisitStore((s) => s.force);
  const unmuteAll = useFirstVisitStore((s) => s.unmuteAll);
  const resetAll = useFirstVisitStore((s) => s.resetAll);

  const guide = getGuideForPath(location.pathname);

  const onClick = () => {
    if (globallyMuted) unmuteAll();
    if (guide) {
      force(guideKey(guide));
      return;
    }
    // No guide for this route — make sure future visits re-prompt.
    resetAll();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx('btn-quiet', globallyMuted ? 'text-gold-bright' : '')}
      data-testid="help-launcher"
      title={
        guide
          ? `Re-open the guide for this page`
          : globallyMuted
            ? `Re-enable first-visit guides`
            : `Reset first-visit guides`
      }
    >
      <HelpCircle size={14} />
      {globallyMuted ? 'Re-enable help' : 'Help'}
    </button>
  );
}
